import React, { Component } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faUtensils } from '@fortawesome/free-solid-svg-icons';
import { HOST } from '../../constants/api';
import { escapeHtml } from '../../utils/sanitize';
import './RecipeBook.css';

const apiGet = (path) => {
  return window.axios.get(`${HOST}${path}`);
};

export default function PublicRecipe() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [recipe, setRecipe] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    if (!token) return;
    setLoading(true);
    apiGet(`/get-public-recipe?token=${encodeURIComponent(token)}`)
      .then((res) => {
        setRecipe(res.data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.response?.data?.message || 'Recipe not found or share link has been revoked.');
        setLoading(false);
      });
  }, [token]);

  const getImageUrl = (imageUrl) => {
    if (!imageUrl) return '';
    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
      return imageUrl.replace('http://', 'https://');
    }
    const backendHost = HOST.endsWith('/api') ? HOST.slice(0, -4) : HOST.replace(/\/api$/, '');
    return backendHost + (imageUrl.startsWith('/') ? imageUrl : '/' + imageUrl);
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#121212', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </div>
    );
  }

  if (error || !recipe) {
    return (
      <div style={{ minHeight: '100vh', background: '#121212', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
        <div style={{ textAlign: 'center', color: '#fff', maxWidth: 400 }}>
          <h2>Recipe Not Found</h2>
          <p style={{ opacity: 0.7 }}>{escapeHtml(error || 'This recipe could not be found.')}</p>
          <Button variant="contained" onClick={() => navigate('/')} sx={{ mt: 2 }}>
            Go Home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#121212', color: '#fff' }}>
      {/* Header */}
      <div style={{ padding: '12px 16px', borderBottom: '1px solid #333', display: 'flex', alignItems: 'center', gap: 12 }}>
        <button
          onClick={() => navigate('/')}
          style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 16, display: 'flex', alignItems: 'center', gap: 6 }}
        >
          <FontAwesomeIcon icon={faArrowLeft} /> Back
        </button>
        <span style={{ fontSize: 14, opacity: 0.6 }}>Shared Recipe</span>
      </div>

      {/* Recipe Content */}
      <div style={{ maxWidth: 800, margin: '0 auto', padding: '20px 16px' }}>
        {/* Title */}
        <h1 style={{ margin: '0 0 8px', fontSize: 24, fontWeight: 700 }}>{escapeHtml(recipe.title)}</h1>

        {/* Source */}
        {recipe.recipe_book && (
          <p style={{ opacity: 0.6, fontSize: 13, margin: '0 0 16px' }}>
            from {escapeHtml(recipe.recipe_book.name)}
            {recipe.creator ? ` by ${escapeHtml(recipe.creator.name)}` : ''}
          </p>
        )}

        {/* Image */}
        {recipe.image_url && (
          <div style={{ borderRadius: 12, overflow: 'hidden', marginBottom: 20, maxHeight: 400 }}>
            <img
              src={getImageUrl(recipe.image_url)}
              alt={escapeHtml(recipe.title)}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          </div>
        )}

        {/* Meta */}
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 20, fontSize: 14, opacity: 0.8 }}>
          {recipe.servings && <span>🍽 {escapeHtml(String(recipe.servings))} servings</span>}
          {recipe.prep_time && <span>⏱ Prep: {escapeHtml(recipe.prep_time)}</span>}
          {recipe.cook_time && <span>🔥 Cook: {escapeHtml(recipe.cook_time)}</span>}
          {recipe.difficulty && <span>Difficulty: {escapeHtml(recipe.difficulty)}</span>}
        </div>

        {/* Description */}
        {recipe.description && (
          <p style={{ fontSize: 14, opacity: 0.8, lineHeight: 1.6, marginBottom: 20 }}>{escapeHtml(recipe.description)}</p>
        )}

        {/* Categories */}
        {recipe.categories && recipe.categories.length > 0 && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 20 }}>
            {recipe.categories.map((cat) => (
              <span
                key={cat.id}
                style={{
                  padding: '4px 12px',
                  borderRadius: 16,
                  fontSize: 12,
                  backgroundColor: (cat.color || '#4caf50') + '33',
                  color: cat.color || '#4caf50',
                }}
              >
                {escapeHtml(cat.name)}
              </span>
            ))}
          </div>
        )}

        {/* Ingredients */}
        {recipe.ingredients && recipe.ingredients.length > 0 && (
          <div style={{ marginBottom: 24 }}>
            <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 12 }}>Ingredients</h2>
            <ul style={{ paddingLeft: 20, lineHeight: 2 }}>
              {recipe.ingredients.map((ing) => (
                <li key={ing.id}>
                  {ing.quantity && <strong>{escapeHtml(ing.quantity)} </strong>}
                  {ing.unit && <span style={{ opacity: 0.7 }}>{escapeHtml(ing.unit)} </span>}
                  <span>{escapeHtml(ing.name)}</span>
                  {ing.notes && <span style={{ opacity: 0.5, fontSize: 12 }}> — {escapeHtml(ing.notes)}</span>}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Directions */}
        {recipe.directions && (
          <div style={{ marginBottom: 24 }}>
            <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 12 }}>Directions</h2>
            <div style={{ lineHeight: 1.8, whiteSpace: 'pre-wrap', fontSize: 14 }}>
              {recipe.directions.split('\n').filter(Boolean).map((step, idx) => (
                <div key={idx} style={{ marginBottom: 12, paddingLeft: 16, borderLeft: '2px solid #333' }}>
                  <strong style={{ opacity: 0.7 }}>Step {idx + 1}:</strong> {escapeHtml(step)}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Notes */}
        {recipe.notes && (
          <div style={{ marginBottom: 24, padding: 16, backgroundColor: '#1e1e1e', borderRadius: 8 }}>
            <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 8, opacity: 0.7 }}>Notes</h3>
            <p style={{ fontSize: 13, lineHeight: 1.6, opacity: 0.8 }}>{escapeHtml(recipe.notes)}</p>
          </div>
        )}
      </div>
    </div>
  );
}
